/**
 * useLabels Hook
 *
 * Fetches email labels with message counts for the active account via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailLabel, EmailCreateLabelInput, EmailUpdateLabelInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useLabels(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.labels(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailLabels(accountId);
    },
    enabled: !!accountId,
  });

  const createLabel = useCallback(
    async (input: EmailCreateLabelInput) => {
      const result = await api.createEmailLabel(input);
      queryClient.invalidateQueries({ queryKey: queryKeys.email.labels(accountId ?? '') });
      return result;
    },
    [accountId, queryClient],
  );

  const updateLabel = useCallback(
    async (labelId: string, input: EmailUpdateLabelInput) => {
      if (!accountId) return;
      const result = await api.updateEmailLabel(labelId, accountId, input);
      queryClient.invalidateQueries({ queryKey: queryKeys.email.labels(accountId) });
      return result;
    },
    [accountId, queryClient],
  );

  const deleteLabel = useCallback(
    async (labelId: string) => {
      if (!accountId) return;
      await api.deleteEmailLabel(labelId, accountId);
      queryClient.invalidateQueries({ queryKey: queryKeys.email.labels(accountId) });
    },
    [accountId, queryClient],
  );

  return {
    labels: queryResult.data?.labels ?? ([] as EmailLabel[]),
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createLabel,
    updateLabel,
    deleteLabel,
  };
}
